import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/balance';
import { CubeRenderer } from './CubeRenderer';
import { TrailRenderer } from './TrailRenderer';
import { TerritoryManager } from './TerritoryManager';
import { ParticleManager } from './ParticleManager';
import { PlayerEffect } from './PlayerEffect';
import { ItemType } from '../config/items';
import { getTextResolution } from '../utils/display';

/**
 * AI 봇 - 자동으로 움직이며 영역 확장
 */
export class AIBot {
  public id: string;
  public x: number;
  public y: number;
  // 활성 아이템 효과 (Game이 effect.getActiveType()로 bot:move에 실어 동기화)
  public readonly effect = new PlayerEffect();
  // 직전 프레임 역방향 여부 (해제 시 급반전 보정용)
  private wasReversed: boolean = false;

  private cubeRenderer: CubeRenderer;
  private trailRenderer: TrailRenderer;
  private territoryManager?: TerritoryManager;
  private particleManager?: ParticleManager;
  private nicknameText: Phaser.GameObjects.Text;
  private effectTimerText: Phaser.GameObjects.Text;
  private nickname: string;

  private speed: number;
  private direction: number = 0;
  private targetDirection: number = 0;
  private size: number;
  private color: number;

  // AI 상태
  private state: 'exploring' | 'returning' = 'exploring';
  private isOutsideTerritory: boolean = false;
  private trailActive: boolean = false;
  private stateTimer: number = 0;
  private maxExploreTime: number = 3000; // 영역 밖에서 최대 시간
  private directionChangeTimer: number = 0;
  private lastInsidePosition: { x: number; y: number } | null = null;
  // 외출(트레일) 한 번 동안 유지하는 회전 방향 (+1 = 시계, -1 = 반시계)
  // 일관된 호를 그리게 해서 자기 꼬리를 가로지르는 랜덤 워크를 막는다
  private turnBias: number = 1;
  private steerTimer: number = 0;

  // 공격형 봇: 감지 범위 안의 플레이어 꼬리를 향해 돌진해 끊는다
  private aggressive: boolean = false;
  private huntTarget: { x: number; y: number } | null = null;
  private huntTimer: number = 0;
  private readonly MAX_HUNT_TIME_MS = 6000; // 너무 오래 쫓으면 포기하고 복귀

  // 사냥 중 랜덤 부스트 (플레이어 부스터와 동일 규칙: x1.5 가속 + 영역 소모 + 꽃가루)
  private boostActive: boolean = false;
  private boostTimer: number = 0;
  private boostCooldown: number = 0;

  // 맵 경계
  private worldCenterX: number;
  private worldCenterY: number;
  private worldRadius: number;

  // 사망 콜백
  private onDeathCallback?: () => void;
  // 영역 확장 완성 콜백 (호스트가 다른 클라이언트에 봇 영역을 브로드캐스트할 때 사용)
  private onTerritoryCompleteCallback?: (trailPoints: { x: number; y: number }[]) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    color: number,
    id: string,
    nickname: string,
    worldCenterX: number,
    worldCenterY: number,
    worldRadius: number
  ) {
    this.id = id;
    this.x = x;
    this.y = y;
    this.color = color;
    this.nickname = nickname;
    this.size = GAME_CONSTANTS.map.tileSize;
    this.speed = GAME_CONSTANTS.movement.baseSpeed * 0.8; // 플레이어보다 약간 느림
    this.worldCenterX = worldCenterX;
    this.worldCenterY = worldCenterY;
    this.worldRadius = worldRadius;

    // 랜덤 초기 방향
    this.direction = Math.random() * Math.PI * 2;
    this.targetDirection = this.direction;

    // 큐브 렌더러
    this.cubeRenderer = new CubeRenderer(scene, this.size, color);
    this.cubeRenderer.setDepth(10);

    // 트레일 렌더러 (큐브 면과 동일한 너비)
    const trailWidth = this.cubeRenderer.getBaseWidth();
    this.trailRenderer = new TrailRenderer(scene, trailWidth, color);

    // 닉네임
    const cubeHeight = this.cubeRenderer.getTotalHeight();
    this.nicknameText = scene.add.text(x, y - cubeHeight - 20, nickname, {
      fontSize: '18px',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 4,
      fontFamily: 'Nunito',
      fontStyle: 'bold',
      resolution: getTextResolution(),
    }).setOrigin(0.5);
    this.nicknameText.setDepth(15);

    // 효과 남은시간 카운터 (닉네임 위)
    this.effectTimerText = scene.add.text(x, 0, '', {
      fontSize: '16px',
      color: '#FFE14E',
      stroke: '#000000',
      strokeThickness: 4,
      fontFamily: 'Nunito',
      fontStyle: 'bold',
      resolution: getTextResolution(),
    }).setOrigin(0.5).setDepth(16).setVisible(false);
  }

  setTerritoryManager(tm: TerritoryManager): void {
    this.territoryManager = tm;
  }

  setParticleManager(pm: ParticleManager): void {
    this.particleManager = pm;
  }

  setOnDeathCallback(callback: () => void): void {
    this.onDeathCallback = callback;
  }

  setOnTerritoryCompleteCallback(callback: (trailPoints: { x: number; y: number }[]) => void): void {
    this.onTerritoryCompleteCallback = callback;
  }

  setAggressive(aggressive: boolean): void {
    this.aggressive = aggressive;
  }

  isAggressive(): boolean {
    return this.aggressive;
  }

  /**
   * 사냥 목표 지점 설정 (Game이 매 프레임 감지 범위 내 가장 가까운 적 꼬리 지점을 전달)
   */
  setHuntTarget(target: { x: number; y: number } | null): void {
    this.huntTarget = target;
  }

  update(delta: number): void {
    // 아이템 효과 만료 + 시각 반영
    this.effect.update(Date.now());
    this.applyEffectVisuals();

    // 역방향 해제 시 급반전(→자기 꼬리 자폭) 방지: 진행 방향 유지
    const isReversed = this.effect.isReversed();
    if (this.wasReversed && !isReversed) {
      this.direction += Math.PI;
      this.targetDirection = this.direction;
    }
    this.wasReversed = isReversed;

    this.updateAI(delta);
    this.move(delta);

    // 큐브 렌더링
    this.cubeRenderer.setDirection(this.direction);
    this.cubeRenderer.render(this.x, this.y);

    // 닉네임 위치
    const cubeHeight = this.cubeRenderer.getTotalHeight();
    this.nicknameText.setPosition(this.x, this.y - cubeHeight - 20);

    // 효과 남은시간 카운터
    const remMs = this.effect.remainingMs(Date.now());
    if (remMs > 0) {
      this.effectTimerText.setText(`⏱ ${Math.ceil(remMs / 1000)}`);
      this.effectTimerText.setPosition(this.x, this.y - cubeHeight - 44);
      this.effectTimerText.setVisible(true);
    } else {
      this.effectTimerText.setVisible(false);
    }
  }

  /** 아이템 효과 적용 (호스트가 봇 감싸 먹기 발동 시) */
  applyEffect(type: ItemType): void {
    this.effect.apply(type, Date.now());
  }

  isInvincibleNow(): boolean {
    return this.effect.isInvincible();
  }

  getSizeMultiplier(): number {
    return this.effect.sizeMultiplier();
  }

  private applyEffectVisuals(): void {
    this.cubeRenderer.setSizeMultiplier(this.effect.sizeMultiplier());
    this.cubeRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.setWidth(this.cubeRenderer.getBaseWidth());
    this.trailRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.tickRainbow();
  }

  private updateAI(delta: number): void {
    if (!this.territoryManager) return;

    // 방향 전환 타이머
    this.directionChangeTimer -= delta;
    if (this.directionChangeTimer <= 0) {
      this.pickNewDirection();
      this.directionChangeTimer = 500 + Math.random() * 1500;
    }

    // 공격형 봇 사냥 모드: 감지된 적 꼬리 지점을 향해 돌진
    // (returning 상태에서는 생존 우선이라 사냥하지 않는다)
    const hunting =
      this.aggressive &&
      this.huntTarget !== null &&
      this.state === 'exploring' &&
      this.huntTimer < this.MAX_HUNT_TIME_MS;

    if (hunting && this.huntTarget) {
      this.huntTimer += delta;
      this.targetDirection = Math.atan2(
        this.huntTarget.y - this.y,
        this.huntTarget.x - this.x
      );
    } else if (!this.huntTarget) {
      // 목표가 사라지면 사냥 시간 회복
      this.huntTimer = Math.max(0, this.huntTimer - delta);
    }

    // 사냥 중 랜덤 부스트 발동/해제
    this.updateHuntBoost(delta, hunting);

    // 자기 꼬리 회피 조향: 목표 방향이 자기 꼬리를 향하면 비껴가도록 보정
    // (특히 returning 상태에서 영역 중심으로 직진하다 꼬리를 가로지르는 자폭 방지)
    this.steerTimer -= delta;
    if (this.steerTimer <= 0) {
      this.steerTimer = 120;
      this.targetDirection = this.steerAwayFromOwnTrail(this.targetDirection);
    }

    // 현재 방향을 목표 방향으로 부드럽게 전환
    const angleDiff = this.normalizeAngle(this.targetDirection - this.direction);
    this.direction += angleDiff * 0.1;

    // 영역 체크
    const wasOutside = this.isOutsideTerritory;
    this.isOutsideTerritory = !this.territoryManager.isInsideOwnTerritory(this.id, this.x, this.y);

    // 영역 안에 있을 때 마지막 위치 저장
    if (!this.isOutsideTerritory) {
      this.lastInsidePosition = { x: this.x, y: this.y };
    }

    // 상태 머신
    switch (this.state) {
      case 'exploring':
        if (!wasOutside && this.isOutsideTerritory) {
          // 영역 밖으로 나감 -> 트레일 시작
          this.trailActive = true;
          this.trailRenderer.clear();
          this.stateTimer = 0;
          // 이번 외출 동안의 회전 방향 결정 (일관된 호 = 자기 꼬리와 안 겹침)
          this.turnBias = Math.random() < 0.5 ? 1 : -1;
          // 꼬리 뿌리 저장 (마지막 영역 안 위치 사용)
          if (this.lastInsidePosition) {
            this.territoryManager?.setTrailRoot(this.id, this.lastInsidePosition.x, this.lastInsidePosition.y);
          }
        }

        if (this.trailActive && this.isOutsideTerritory) {
          this.trailRenderer.addPoint(this.x, this.y, this.direction);
          this.stateTimer += delta;

          // 적 영역 위를 지나갈 때 파편 효과
          const enemyColor = this.territoryManager.getTerritoryColorAt(this.x, this.y);
          if (enemyColor !== null && this.particleManager) {
            this.particleManager.debrisSpray(this.x, this.y, this.direction, enemyColor);
          }

          // 자기 꼬리 충돌 체크
          if (this.checkSelfTrailCollision()) {
            console.log(`Bot ${this.id} self-trail collision (exploring)`);
            this.onDeathCallback?.();
            return;
          }

          // 일정 시간 후 돌아가기 (사냥 중에는 MAX_HUNT_TIME까지 추격 허용)
          if (this.stateTimer > this.maxExploreTime && !hunting) {
            this.state = 'returning';
          }
        }
        break;

      case 'returning':
        // 영역 방향으로 이동
        const territory = this.territoryManager.getTerritory(this.id);
        if (territory && territory.points.length > 0) {
          // 영역 중심 방향으로
          let cx = 0, cy = 0;
          for (const p of territory.points) {
            cx += p.x;
            cy += p.y;
          }
          cx /= territory.points.length;
          cy /= territory.points.length;
          // 매 프레임 덮어쓰는 경로이므로 회피 보정도 즉시 적용해야 한다
          // (주기 조향에만 맡기면 중심 직진이 회피각을 계속 되돌려 자폭함)
          this.targetDirection = this.steerAwayFromOwnTrail(
            Math.atan2(cy - this.y, cx - this.x)
          );
        }

        if (this.trailActive && this.isOutsideTerritory) {
          this.trailRenderer.addPoint(this.x, this.y, this.direction);

          // 적 영역 위를 지나갈 때 파편 효과
          const enemyColor = this.territoryManager?.getTerritoryColorAt(this.x, this.y);
          if (enemyColor !== null && enemyColor !== undefined && this.particleManager) {
            this.particleManager.debrisSpray(this.x, this.y, this.direction, enemyColor);
          }

          if (this.checkSelfTrailCollision()) {
            console.log(`Bot ${this.id} self-trail collision (returning)`);
            this.onDeathCallback?.();
            return;
          }
        }

        // 영역 안으로 돌아왔을 때
        if (wasOutside && !this.isOutsideTerritory && this.trailActive) {
          this.completeTerritory();
          this.state = 'exploring';
          this.maxExploreTime = 2000 + Math.random() * 3000;
        }
        break;
    }
  }

  private pickNewDirection(): void {
    if (this.state === 'returning') return;

    // 맵 경계 체크
    const distToCenter = Phaser.Math.Distance.Between(this.x, this.y, this.worldCenterX, this.worldCenterY);

    if (distToCenter > this.worldRadius * 0.7) {
      // 경계 근처면 중심 방향으로
      this.targetDirection = Math.atan2(this.worldCenterY - this.y, this.worldCenterX - this.x);
      this.targetDirection += (Math.random() - 0.5) * Math.PI / 2;
    } else if (this.trailActive && this.isOutsideTerritory) {
      // 트레일 중에는 정해진 회전 방향으로만 호를 그린다
      // (양방향 랜덤 전환은 자기 꼬리를 가로지르는 루프를 만들었음)
      this.targetDirection = this.direction + this.turnBias * (Math.PI / 6 + Math.random() * Math.PI / 4);
    } else {
      // 영역 안에서는 자유로운 랜덤 방향 변경
      this.targetDirection = this.direction + (Math.random() - 0.5) * Math.PI / 2;
    }
  }

  /**
   * 사냥 중 랜덤 부스트: 평균 1초에 한 번꼴로 발동해 0.5~1.4초 돌진한다.
   * 플레이어와 동일하게 영역이 충분할 때만 가능하고(canBoost), 사용 중 영역을 소모한다.
   */
  private updateHuntBoost(delta: number, hunting: boolean): void {
    this.boostCooldown = Math.max(0, this.boostCooldown - delta);

    if (this.boostActive) {
      this.boostTimer -= delta;
      const canKeep =
        hunting && (!this.territoryManager || this.territoryManager.canBoost(this.id));
      if (this.boostTimer <= 0 || !canKeep) {
        this.boostActive = false;
        this.boostCooldown = 800 + Math.random() * 1500;
      }
      return;
    }

    if (!hunting || this.boostCooldown > 0) return;
    if (this.territoryManager && !this.territoryManager.canBoost(this.id)) return;

    // 프레임 독립 확률 (delta 비례) — 평균 ~1초에 한 번 발동
    if (Math.random() < delta / 1000) {
      this.boostActive = true;
      this.boostTimer = 500 + Math.random() * 900;
    }
  }

  private move(delta: number): void {
    // 부스트 중에는 플레이어와 동일하게 영역을 소모하고 꽃가루 효과를 낸다
    if (this.boostActive) {
      this.territoryManager?.consumeTerritory(this.id, delta);
      this.particleManager?.boostTrail(this.x, this.y, this.direction, this.color);
    }

    // 아이템 속도 배율 + 역방향 적용
    const currentSpeed =
      (this.boostActive ? this.speed * 1.5 : this.speed) * this.effect.speedMultiplier();
    const moveDir = this.effect.isReversed() ? this.direction + Math.PI : this.direction;
    const moveX = Math.cos(moveDir) * currentSpeed * (delta / 1000);
    const moveY = Math.sin(moveDir) * currentSpeed * (delta / 1000);

    let newX = this.x + moveX;
    let newY = this.y + moveY;

    // 원형 맵 경계 체크
    const dist = Phaser.Math.Distance.Between(newX, newY, this.worldCenterX, this.worldCenterY);
    if (dist > this.worldRadius - 30) {
      const angle = Math.atan2(newY - this.worldCenterY, newX - this.worldCenterX);
      newX = this.worldCenterX + Math.cos(angle) * (this.worldRadius - 30);
      newY = this.worldCenterY + Math.sin(angle) * (this.worldRadius - 30);
      // 경계에 닿으면 방향 전환
      this.targetDirection = angle + Math.PI + (Math.random() - 0.5) * Math.PI / 2;
    }

    this.x = newX;
    this.y = newY;
  }

  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }

  /**
   * 목표 방향이 자기 꼬리와 충돌할지 전방 샘플링으로 예측하고,
   * 충돌이 예상되면 turnBias 방향 우선으로 15°씩 비껴 안전한 각도를 찾는다.
   */
  private steerAwayFromOwnTrail(desired: number): number {
    if (!this.trailActive || !this.isOutsideTerritory) return desired;

    const trailPoints = this.trailRenderer.getPoints();
    if (trailPoints.length < 12) return desired;

    // 머리 근처 최근 포인트는 항상 닿아 있으므로 제외
    const checkUpTo = trailPoints.length - 8;
    const clearance = this.size * 0.8;

    const isHeadingSafe = (dir: number): boolean => {
      for (const lookahead of [40, 80, 130]) {
        const sx = this.x + Math.cos(dir) * lookahead;
        const sy = this.y + Math.sin(dir) * lookahead;

        for (let i = 0; i < checkUpTo - 1; i++) {
          const p1 = trailPoints[i];
          const p2 = trailPoints[i + 1];
          if (this.pointToSegmentDistance(sx, sy, p1.x, p1.y, p2.x, p2.y) < clearance) {
            return false;
          }
        }
      }
      return true;
    };

    if (isHeadingSafe(desired)) return desired;

    for (let step = 1; step <= 12; step++) {
      const offset = (Math.PI / 12) * step; // 15°씩 확대
      const biased = desired + this.turnBias * offset;
      if (isHeadingSafe(biased)) return biased;
      const opposite = desired - this.turnBias * offset;
      if (isHeadingSafe(opposite)) return opposite;
    }

    // 사방이 막힌 극단적 상황: 회전 방향으로 직각 회피
    return desired + this.turnBias * (Math.PI / 2);
  }

  private checkSelfTrailCollision(): boolean {
    const trailPoints = this.trailRenderer.getPoints();
    if (trailPoints.length < 10) return false;

    const collisionRadius = this.size * 0.3;
    const checkUpTo = trailPoints.length - 5;

    for (let i = 0; i < checkUpTo - 1; i++) {
      const p1 = trailPoints[i];
      const p2 = trailPoints[i + 1];
      const dist = this.pointToSegmentDistance(this.x, this.y, p1.x, p1.y, p2.x, p2.y);

      if (dist < collisionRadius) {
        return true;
      }
    }

    return false;
  }

  private pointToSegmentDistance(
    px: number, py: number,
    x1: number, y1: number,
    x2: number, y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }

    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));

    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;

    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
  }

  private completeTerritory(): void {
    if (!this.territoryManager) {
      this.resetTrail();
      return;
    }

    const trailPoints = this.trailRenderer.getPoints();
    if (trailPoints.length >= 3) {
      this.territoryManager.expandTerritory(this.id, trailPoints);
      this.onTerritoryCompleteCallback?.(trailPoints.slice());
    }

    this.resetTrail();
  }

  private resetTrail(): void {
    this.trailActive = false;
    this.trailRenderer.clear();
    this.territoryManager?.clearTrailRoot(this.id);
  }

  getTrailPoints(): { x: number; y: number }[] {
    return this.trailRenderer.getPoints();
  }

  isTrailActive(): boolean {
    return this.trailActive;
  }

  getColor(): number {
    return this.color;
  }

  getDirection(): number {
    return this.direction;
  }

  getNickname(): string {
    return this.nickname;
  }

  private hasCrown: boolean = false;

  setCrown(hasCrown: boolean): void {
    if (this.hasCrown === hasCrown) return;
    this.hasCrown = hasCrown;
    const displayText = hasCrown ? `👑 ${this.nickname}` : this.nickname;
    this.nicknameText.setText(displayText);
  }

  respawn(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.resetTrail();
    this.state = 'exploring';
    this.isOutsideTerritory = false;
    this.boostActive = false;
    this.boostTimer = 0;
    this.huntTimer = 0;
    this.effect.clear();
    this.cubeRenderer.setSizeMultiplier(1);
    this.trailRenderer.setRainbow(false);
    this.direction = Math.random() * Math.PI * 2;
    this.targetDirection = this.direction;
  }

  /**
   * 봇 정체성 리셋 (새로운 색상과 닉네임)
   */
  resetIdentity(newColor: number, newNickname: string): void {
    this.color = newColor;
    this.nickname = newNickname;

    // 큐브 렌더러 색상 변경
    this.cubeRenderer.setColor(newColor);

    // 트레일 렌더러 색상 변경
    this.trailRenderer.setColor(newColor);

    // 닉네임 텍스트 업데이트
    this.nicknameText.setText(newNickname);
  }

  destroy(): void {
    this.cubeRenderer.destroy();
    this.trailRenderer.destroy();
    this.nicknameText.destroy();
    this.effectTimerText.destroy();
  }
}
